// Typed wrappers around the Stripe-backed surface of the billing route
// plugin. The customer-facing /v1/usage endpoint lives in its own wrapper
// (lib/api/usage.ts) — this file is just for the portal handoff and the
// invoices list, which are the two things the Billing page renders.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ChangeSubscriptionPlanInput,
  ChangeSubscriptionPlanResult,
  CreateCheckoutSessionInput,
  CreateCheckoutSessionResult,
  CreatePortalSessionInput,
  CreatePortalSessionResult,
  DowngradePreview,
  ListInvoicesResult,
  ResumeSubscriptionResult,
} from '@cortex/shared';
import type { ApiClient } from '../api-client.js';
import { useApiClient } from '../providers.js';
import { USAGE_QUERY_KEY } from './usage.js';
import { AGENTS_QUERY_KEY } from './agents.js';

export const billingApi = {
  createPortalSession: (api: ApiClient, body: CreatePortalSessionInput) =>
    api.post<CreatePortalSessionResult>('/v1/billing/portal', body),
  createCheckoutSession: (api: ApiClient, body: CreateCheckoutSessionInput) =>
    api.post<CreateCheckoutSessionResult>('/v1/billing/checkout', body),
  changeSubscriptionPlan: (api: ApiClient, body: ChangeSubscriptionPlanInput) =>
    api.post<ChangeSubscriptionPlanResult>('/v1/billing/subscription', body),
  // Clear `cancel_at_period_end` on a scheduled-to-cancel sub. Mirrors what
  // Stripe Portal's "Don't cancel subscription" button does — exposed here
  // so the dashboard's Resume CTA doesn't have to bounce out to the Portal.
  resumeSubscription: (api: ApiClient) =>
    api.post<ResumeSubscriptionResult>('/v1/billing/subscription/resume', {}),
  listInvoices: (api: ApiClient, limit?: number) =>
    api.get<ListInvoicesResult>(
      limit !== undefined ? `/v1/billing/invoices?limit=${String(limit)}` : '/v1/billing/invoices',
    ),
  // Pre-flight snapshot for the downgrade modal. Returns the over-cap diff
  // for a target plan; the dashboard renders the agent checkbox list with
  // `preselectedActive` = default-checked.
  previewDowngrade: (api: ApiClient, targetPlan: string) =>
    api.get<DowngradePreview>(
      `/v1/billing/subscription/preview?targetPlan=${encodeURIComponent(targetPlan)}`,
    ),
} as const;

export const INVOICES_QUERY_KEY = ['billing', 'invoices'] as const;

// Shared mutation hook for the Resume CTA. Used on the billing page (Phase
// 5.5 default), the home retention card, and the playground 402 panel.
// Each callsite handles `error` / `isPending` locally; the hook centralises:
//   • the POST → /v1/billing/subscription/resume call
//   • a 1.5s wait for `customer.subscription.updated` to land and clear
//     cancel_at on the row (otherwise an immediate refetch races the webhook)
//   • invalidating USAGE_QUERY_KEY so every consumer (banner, card, panel)
//     re-renders without the cancel state
//
// Without the wait, the cancel banner would briefly flicker back during the
// race window — the row still has cancel_at set when the refetch fires,
// then clears half a second later, leaving the operator confused about
// whether the click actually worked.
export function useResumeSubscription() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.resumeSubscription(api),
    onSuccess: async () => {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await queryClient.invalidateQueries({ queryKey: USAGE_QUERY_KEY });
    },
  });
}

// Re-enable a previously soft-disabled agent. Bumps both the agents list
// and the usage summary so the disabled-resources banner counter and the
// agents page row state both clear in lockstep.
export function useEnableAgent() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) =>
      api.post(`/v1/agents/${agentId}/enable`, {}),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: USAGE_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY }),
      ]);
    },
  });
}

// Re-include a previously soft-excluded KB document into retrieval. The
// agentId in the path is the agent that owns the KB — every KB doc belongs
// to exactly one KB which belongs to one agent at a time, so the dashboard
// already has the agentId when rendering the doc row.
export function useIncludeKbDocument() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, docId }: { agentId: string; docId: string }) =>
      api.post(`/v1/agents/${agentId}/knowledge/documents/${docId}/include`, {}),
    onSuccess: async () => {
      // KB list and usage summary both depend on this — kbDocsCount and
      // excludedKbDocCount move together.
      await queryClient.invalidateQueries({ queryKey: USAGE_QUERY_KEY });
      // Knowledge queries are scoped per-agent; broad invalidate so any
      // open agent's list re-renders without specific keys here.
      await queryClient.invalidateQueries({ queryKey: ['knowledge'] });
    },
  });
}
